import { useState } from "react";
import { Check, ChevronDown, MoreHorizontal, Building2, MessageSquare } from "lucide-react";
import { T, TYPES, STATUS, MENU_SHADOW, ddMon, relativeLine } from "./todoTokens";
import { recordHref } from "../../../lib/recordLinks";
import { DueDatePicker } from "./DueDatePicker";

const AV_TONE = {
  known: { bg: "#F1EEE6", border: `1px solid ${T.cardEdge}`, color: T.ink2 },
  mine: { bg: T.accent, border: `1px solid ${T.accentEdge}`, color: T.accentInk },
  missing: { bg: "#FDECE5", border: "1px dashed #E3A58C", color: "#A8320A" },
  contact: { bg: "#fff", border: `1px dashed ${T.cardEdge}`, color: T.ink2 },
  team: { bg: "#F1EEE6", border: `1px dashed ${T.cardEdge}`, color: T.ink2 },
};

const Avatar = ({ a, isMe }) => {
  const s = AV_TONE[isMe ? "mine" : a.tone] || AV_TONE.known;
  return (
    <span className="inline-flex items-center justify-center rounded-full shrink-0"
      style={{ width: 20, height: 20, fontSize: 9, fontWeight: 700, fontFamily: "Manrope, sans-serif",
        background: s.bg, border: s.border, color: s.color }}>{a.initials}</span>
  );
};

/* Repeat slippage shouldn't hide — the chip's tooltip carries the whole trail. */
const slipTrail = (t) => (t.due_date_history || [])
  .map((s) => `${ddMon(s.from)} → ${ddMon(s.to)} · ${s.reason || "no reason"} (${s.by || "?"})`)
  .join("\n") || `Moved ${t.slip_count}×`;

/* Directive §9 — one row. Kept as a component with its own local UI state so
   typing a comment or opening a menu never re-renders the whole list. */
export const TodoRow = ({ t, today, thisYear, myName, density, selected, onSelect,
                          onStatus, onDate, onComment, onEdit, onDelete, onRecordCheck }) => {
  const [menu, setMenu] = useState(null);          // 'status' | 'row' | null
  const [showPoints, setShowPoints] = useState(false);
  const [draft, setDraft] = useState("");
  const [commenting, setCommenting] = useState(false);

  const type = TYPES[t.type] || TYPES.action;
  const st = STATUS[t.status] || STATUS.open;
  const rel = relativeLine(t.due_date, today, t.status);
  const done = t.status === "done";
  const isHealth = t.type === "health_check";
  const locked = !t.can_edit;
  const a = t.assignee || { name: "Unassigned", initials: "?", tone: "missing" };
  const isMe = !!myName && a.name === myName;
  const points = t.points || [];
  const pad = density === "compact" ? "9px 20px" : "14px 20px";

  const close = () => setMenu(null);

  return (
    <div style={{ borderTop: `1px solid ${T.rule}` }} data-testid={`todo-row-${t.id}`}>
      <div className="todo-row-grid items-start"
        style={{ columnGap: 14, padding: pad,
          opacity: done ? 0.55 : 1,
          boxShadow: t.overdue && !done ? `inset 3px 0 0 ${T.overdue}` : undefined }}>

        <label className="tr-sel flex items-center justify-center h-[22px] cursor-pointer">
          <input type="checkbox" checked={selected} onChange={() => onSelect(t.id)}
            disabled={locked} data-testid={`todo-select-${t.id}`} />
        </label>

        <button onClick={() => !locked && !isHealth && onStatus(t, done ? "open" : "done")}
          disabled={locked || isHealth}
          title={isHealth ? "Record a health check to close this" : done ? "Reopen" : "Mark done"}
          className="tr-chk rounded-full flex items-center justify-center transition-colors group disabled:cursor-not-allowed"
          style={{ width: 22, height: 22, border: `1.5px solid ${done ? T.doneFill : T.cardEdge}`,
            background: done ? T.doneFill : "#fff" }}
          data-testid={`todo-complete-${t.id}`}>
          <Check className="w-3 h-3" style={{ color: done ? "#fff" : "transparent" }} />
        </button>

        <div className="tr-body min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-[14px] font-semibold" style={{ color: T.ink, lineHeight: 1.4,
              textWrap: "pretty", textDecoration: done ? "line-through" : "none" }}>{t.title}</span>
            <Tag type={type} id={t.id} tkey={t.type} />
            {t.required && !done && (
              <span className="whitespace-nowrap" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                background: "#FDECE5", border: "1px solid #F3C9B7", color: "#A8320A", borderRadius: 4, padding: "1px 6px" }}
                data-testid={`todo-required-${t.id}`}>REQUIRED</span>
            )}
          </div>

          {points.length > 1 && (
            <>
              <button onClick={() => setShowPoints((v) => !v)} className="text-[12.5px] font-semibold mt-0.5 hover:underline"
                style={{ color: "#8A6D00" }} data-testid={`todo-points-toggle-${t.id}`}>
                {showPoints ? "Hide points" : `+${points.length - 1} more points`}
              </button>
              {showPoints && (
                <ul className="mt-1 pl-3 space-y-1" style={{ borderLeft: `2px solid ${T.cardEdge}` }}
                  data-testid={`todo-points-${t.id}`}>
                  {points.slice(1).map((p, i) => <li key={i} className="text-[13px]" style={{ color: T.ink2 }}>{p}</li>)}
                </ul>
              )}
            </>
          )}

          <div className="flex items-center flex-wrap mt-1 text-[12.5px]" style={{ gap: 14, color: T.muted }}>
            <span className="inline-flex items-center gap-1.5">
              <Avatar a={a} isMe={isMe} />
              <span style={{ color: a.tone === "missing" ? "#A8320A" : T.ink, fontWeight: 500 }}>{a.name}</span>
              {a.role && <span className="text-[11px]">{a.role}</span>}
            </span>
            {t.link_name && (
              recordHref(t.link_type, t.link_id) ? (
                <a href={recordHref(t.link_type, t.link_id)} onClick={(e) => e.stopPropagation()}
                  title={`Open ${t.link_name}`} data-testid={`todo-link-${t.id}`}
                  className="inline-flex items-center gap-1 min-w-0 hover:underline"
                  style={{ color: T.ink, fontWeight: 500 }}>
                  <Building2 className="w-3 h-3 shrink-0" /> <span className="truncate">{t.link_name}</span>
                </a>
              ) : (
                <span className="inline-flex items-center gap-1 min-w-0">
                  <Building2 className="w-3 h-3 shrink-0" /> <span className="truncate">{t.link_name}</span>
                </span>
              )
            )}
            {t.source_label && <span>{t.source_label}</span>}
            {(t.comments || []).length > 0 && (
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> {t.comments.length}
              </span>
            )}
          </div>

          {(t.comments || []).length > 0 && (() => {
            const c = t.comments[t.comments.length - 1];
            return (
              <div className="mt-1 text-[12.5px]" style={{ color: T.ink2 }}>
                <span className="font-semibold">{c.author === myName ? "You" : c.author}:</span> {c.text}
              </div>
            );
          })()}
        </div>

        <DueDatePicker id={t.id} value={t.due_date} disabled={locked}
          onPick={(d) => onDate(t, d)}>
          <button title={isHealth ? "Change due date — this moves the client's next check" : "Change due date"}
            className="tr-due text-left rounded-[7px] px-1.5 py-0.5 border border-transparent hover:border-[#DAD5CA] hover:bg-[#FBFAF7] disabled:hover:border-transparent disabled:hover:bg-transparent"
            data-testid={`todo-due-${t.id}`}>
            <div className="mono text-[12.5px] font-bold"
              style={{ color: !t.due_date ? T.faint : t.overdue && !done ? T.overdue : T.ink }}>
              {ddMon(t.due_date, thisYear)}
            </div>
            <div className="text-[11.5px]" style={{ color: rel.color, fontWeight: rel.bold ? 600 : 400 }}>{rel.text}</div>
            {t.slip_count > 0 && (
              <div className="mono text-[10px] font-bold mt-0.5 inline-block rounded-[4px] px-1"
                style={{ background: "#FFF4D6", border: "1px solid #E8C86A", color: "#8A6D00" }}
                title={slipTrail(t)} data-testid={`todo-slips-${t.id}`}>moved {t.slip_count}×</div>
            )}
          </button>
        </DueDatePicker>

        <div className="tr-status relative">
          {isHealth ? (
            <button onClick={() => onRecordCheck(t)} data-testid={`todo-record-check-${t.id}`}
              className="w-full h-[30px] rounded-full text-[12px] font-semibold"
              style={{ background: type.fill, border: `1px solid ${type.border}`, color: type.ink }}>
              Record check
            </button>
          ) : (
            <>
              <button onClick={() => setMenu(menu === "status" ? null : "status")} disabled={locked}
                className="w-full h-[30px] rounded-full text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                style={{ background: st.fill, border: `1px solid ${st.border}`, color: st.ink }}
                data-testid={`todo-status-${t.id}`}>
                <span style={{ width: 7, height: 7, borderRadius: 999,
                  background: st.dot || "transparent", border: st.dot ? "none" : `1.5px solid ${st.ring}` }} />
                {st.label} <ChevronDown className="w-3 h-3" />
              </button>
              {menu === "status" && (
                <Menu width={160} onClose={close} testid={`todo-status-menu-${t.id}`}>
                  {["open", "in_progress", "done"].map((s) => (
                    <button key={s} onClick={() => { onStatus(t, s); close(); }}
                      className="w-full text-left h-[34px] px-3 text-[13px] hover:bg-[#F5F2EA]"
                      style={{ background: t.status === s ? T.menuHover : undefined,
                        fontWeight: t.status === s ? 700 : 400, color: T.ink }}
                      data-testid={`todo-status-opt-${s}-${t.id}`}>{STATUS[s].label}</button>
                  ))}
                </Menu>
              )}
            </>
          )}
        </div>

        <div className="tr-menu relative">
          <button onClick={() => setMenu(menu === "row" ? null : "row")}
            className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F5F2EA]"
            style={{ color: T.ink2 }} data-testid={`todo-menu-${t.id}`}>
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menu === "row" && (
            <Menu width={210} right onClose={close} testid={`todo-row-menu-${t.id}`}>
              <button onClick={() => { setCommenting(true); close(); }}
                className="w-full text-left h-[34px] px-3 text-[13px] hover:bg-[#F5F2EA]" style={{ color: T.ink }}
                data-testid={`todo-menu-comment-${t.id}`}>Comment</button>
              {!locked && (
                <button onClick={() => { onEdit(t); close(); }}
                  className="w-full text-left h-[34px] px-3 text-[13px] hover:bg-[#F5F2EA]" style={{ color: T.ink }}
                  data-testid={`todo-menu-edit-${t.id}`}>Edit</button>
              )}
              {t.open_label && (
                <a href={t.open_path} className="block h-[34px] px-3 text-[13px] leading-[34px] hover:bg-[#F5F2EA]"
                  style={{ color: T.ink }} data-testid={`todo-menu-open-${t.id}`}>{t.open_label}</a>
              )}
              {!locked && (
                <button onClick={() => { onDelete(t); close(); }}
                  className="w-full text-left h-[34px] px-3 text-[13px] hover:bg-[#F5F2EA]"
                  style={{ color: "#A8320A", borderTop: `1px solid ${T.rule}` }}
                  data-testid={`todo-menu-delete-${t.id}`}>Delete</button>
              )}
            </Menu>
          )}
        </div>
      </div>

      {commenting && (
        <div className="px-5 pb-3" style={{ paddingLeft: 78 }}>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} autoFocus
            placeholder="Add a comment. The assignee is notified."
            className="w-full rounded-[7px] p-2 text-[13px]" style={{ border: `1px solid ${T.cardEdge}` }}
            data-testid={`todo-comment-input-${t.id}`} />
          <div className="flex items-center gap-2 mt-1.5">
            <button disabled={!draft.trim()} onClick={async () => { await onComment(t, draft); setDraft(""); setCommenting(false); }}
              className="h-8 px-3 rounded-[7px] text-[12.5px] font-semibold disabled:opacity-50"
              style={{ background: draft.trim() ? T.accent : "#E6E2D9", color: T.accentInk }}
              data-testid={`todo-comment-post-${t.id}`}>Post</button>
            <button onClick={() => { setDraft(""); setCommenting(false); }}
              className="h-8 px-3 rounded-[7px] text-[12.5px]" style={{ color: T.ink2 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Tag = ({ type, id, tkey }) => (
  <span className="whitespace-nowrap" style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 4, padding: "1px 6px",
    background: type.fill, color: type.ink, border: `1px solid ${type.border}` }}
    data-testid={`todo-type-tag-${tkey}-${id}`}>{type.label}</span>
);

const Menu = ({ width, right, children, onClose, testid }) => (
  <>
    <div className="fixed inset-0 z-40" onClick={onClose} />
    <div className="absolute z-50 mt-1 rounded-[8px] overflow-hidden"
      style={{ width, [right ? "right" : "left"]: 0, top: "100%", background: "#fff",
        border: `1px solid ${T.cardEdge}`, boxShadow: MENU_SHADOW }} data-testid={testid}>
      {children}
    </div>
  </>
);
